/**
 * Real Twig rendering for the email preview panes, built on twig.js.
 *
 * Editor-implementation agnostic on purpose: any future body editor (rich
 * text, drag-and-drop) renders its preview through this module. Semantics:
 *
 * - Variables come in as the flat dot-path map the preview UI already uses
 *   ("customer.firstname" → "Max") and are expanded to the nested context
 *   Twig expects.
 * - Unknown variables keep their raw `{{ path }}` text (prefilled into the
 *   context), matching the long-standing "approximate preview" behavior and
 *   keeping conditional branches visible by default.
 * - The partner `|trans` filter resolves from the provided translation map;
 *   unknown or empty keys keep their raw-looking `{{ 'key' | trans }}` text.
 * - DB-loaded fragments (GET /template-fragments, cached in Redux) are
 *   registered in twig.js's inline-template registry under their fragment
 *   keys, so `{% include 'partials/…' %}`, `{% extends %}` and `{% block %}`
 *   overrides resolve exactly like the server's DatabaseFragmentLoader. A
 *   `layoutKey` is injected as `{% extends %}` the same way the backend does
 *   (EmailTemplate::renderEmail), including the `_subject` / `_pretext`
 *   context the layouts read.
 * - When rendering still fails (mid-edit syntax errors, a missing fragment),
 *   the naive substitution keeps the preview alive.
 */
import Twig from 'twig';
import {
  extractIfConditionVars,
  extractVariablePaths,
  extractConcatenatedPaths,
  stripEmptyComments,
  substitutePreviewVars,
  substituteTransFilters,
} from './insertion';

export interface TwigPreviewOptions {
  /** Flat dot-path sample values ("customer.firstname" → "Max"). */
  context?: Record<string, string>;
  /** Partner translations for the `|trans` filter ("group.key" → value). */
  translations?: Record<string, string>;
  /** Fragment key → Twig source, for `{% include %}` / `{% extends %}`. */
  fragments?: Record<string, string>;
  /** Applied as `{% extends %}` around the body, mirroring the backend. */
  layoutKey?: string | null;
  /** Rendered subject/pretext exposed to layouts as `_subject`/`_pretext`. */
  subjectText?: string;
  pretextText?: string;
  /**
   * Extra Twig sources whose `{{ path }}` variables are prefilled as raw
   * placeholders like the main source's. The fragment editor passes the
   * draft fragment here: its preview renders through an `{% include %}`
   * wrapper, so without this the draft's own variables would silently
   * render empty instead of staying visible and fillable.
   */
  prefillSources?: string[];
}

export interface TwigPreviewResult {
  html: string;
  /** Set when Twig rendering failed and the naive fallback was used. */
  error?: string;
  /**
   * `{{ path }}` placeholders still visible in the final output — includes
   * provider merge tags contributed by fragments (Event_start_date, …) that
   * never appear in the body source. Feeds the test-data panel.
   */
  placeholderVariables: string[];
  /**
   * Variables that control `{% if %}` branches: referenced in the source,
   * the layout or any reachable fragment (real Twig conditions twig.js
   * evaluates), plus literal provider-side merge conditionals surviving in
   * the output. Sample values toggle the branches — 'false'/'0' force a
   * branch off, any other value turns it on.
   */
  conditionVariables: string[];
}

/**
 * The translations visible to the module-level `trans` filter during a
 * render. twig.js filters are registered globally and only receive
 * (value, args), so per-render state travels through this variable —
 * rendering is synchronous, making this safe.
 */
let activeTranslations: Record<string, string> = {};

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

function decodeHtmlEntities(value: string): string {
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }
  return value
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) =>
      String.fromCodePoint(parseInt(hex, 16)),
    )
    .replace(/&#(\d+);/g, (_, code: string) =>
      String.fromCodePoint(parseInt(code, 10)),
    )
    .replace(
      /&([a-z]+);/gi,
      (match, name: string) => NAMED_ENTITIES[name.toLowerCase()] ?? match,
    );
}

/**
 * PHP Twig templates in this codebase emit literal `{{ … }}` merge tags for
 * the downstream mail provider via `{{'{{'}} … {{'}}'}}`. twig.js's tokenizer
 * cannot parse a `}}` inside a string literal of an output tag, so these
 * expressions are swapped for private-use sentinels before compilation and
 * restored in the rendered HTML. The quotes may be backslash-escaped
 * (`{{\'{{\'}}`) when the expression sits inside a quoted partial parameter.
 */
const LITERAL_OPEN_BRACE = '';
const LITERAL_CLOSE_BRACE = '';
const OPEN_BRACE_EXPRESSION = /\{\{\s*\\?(['"])\{\{\\?\1\s*\}\}/g;
const CLOSE_BRACE_EXPRESSION = /\{\{\s*\\?(['"])\}\}\\?\1\s*\}\}/g;

/**
 * `{% include('x') with {…} %}` → `{% include 'x' with {…} %}`.
 *
 * PHP Twig accepts the parenthesised form in the tag — the argument is just an
 * expression — and several seeded partials use it
 * (`general/partials/section_with_image` and `…_v2`). twig.js does not: it takes
 * the whole `('x')` as the path, fails to resolve it, and throws
 * *"Unable to find template file"* naming the **outer** fragment, which is why
 * this reads as "the partial is broken" rather than "one include is unparsable".
 *
 * Normalised at registration rather than fixed in the twig files: the files are
 * valid Twig and render correctly on the server, so the defect is the preview's.
 */
const PAREN_INCLUDE_TAG = /(\{%-?\s*include)\s*\(\s*(['"][^'"]+['"])\s*\)/g;

function escapeBraceLiterals(source: string): string {
  return source
    .replace(PAREN_INCLUDE_TAG, '$1 $2')
    .replace(OPEN_BRACE_EXPRESSION, LITERAL_OPEN_BRACE)
    .replace(CLOSE_BRACE_EXPRESSION, LITERAL_CLOSE_BRACE);
}

function restoreBraceLiterals(rendered: string): string {
  return rendered
    .replace(new RegExp(LITERAL_OPEN_BRACE, 'g'), '{{')
    .replace(new RegExp(LITERAL_CLOSE_BRACE, 'g'), '}}');
}

/**
 * Provider-side merge tags are *text* in the literal pass below — they must
 * reach the mail provider verbatim — so they are swapped for sentinels before
 * that render and put back after it. Only the `{% … %}` logic is handed to
 * twig.js; `{{ … }}` keeps the substitution semantics `substitutePreviewVars`
 * defines (known path filled, everything else left alone), which also means a
 * provider tag carrying provider-only filters can never be rendered to empty.
 */
const OUTPUT_TAG = /\{\{[\s\S]*?\}\}/g;
const PROTECTED_TAG = /\uE100(\d+)\uE101/g;

function protectOutputTags(html: string): [string, string[]] {
  const tags: string[] = [];
  const protectedHtml = html.replace(OUTPUT_TAG, (tag) => {
    tags.push(tag);
    return `\uE100${tags.length - 1}\uE101`;
  });
  return [protectedHtml, tags];
}

function restoreOutputTags(html: string, tags: string[]): string {
  return html.replace(
    PROTECTED_TAG,
    (match, index: string) => tags[Number(index)] ?? match,
  );
}

/**
 * Evaluates the *literal* `{% … %}` blocks that survive the main render: the
 * provider-side merge conditionals a template emits through brace-literal
 * expressions (`{{'{%'}} if Branch_id == "04" {{'%}'}}`), against the sample
 * values — so the preview reads as prose instead of showing raw tags.
 *
 * twig.js does the evaluating: it is the same engine the main pass uses, and it
 * gets `elseif`, nesting, operator precedence and `is defined` right. The
 * hand-rolled regex this replaces did not — a lazy match to the first
 * `{% else %}`/`{% endif %}` swallowed any `{% elseif %}` clause into the
 * if-branch, so a three-way greeting rendered two of its branches plus the raw
 * elseif tag.
 *
 * A condition whose variables have no sample value sees each variable's own
 * raw `{{ path }}` text, so bare-truthiness blocks (`{% if Event_service %}`)
 * still show their content by default rather than vanishing from an untouched
 * preview; comparisons against a literal resolve for real and fall through to
 * `{% else %}`. 'false' and '0' count as off, as everywhere else in the panel.
 *
 * On a compile or render error the input comes back untouched: provider syntax
 * twig.js cannot parse has to stay visible as text, not blank the preview.
 */
export function evaluateLiteralConditionals(
  html: string,
  context: Record<string, string> = {},
): string {
  if (!html.includes('{%')) return html;

  registerFilters();
  const [protectedHtml, tags] = protectOutputTags(html);

  const flatContext: Record<string, unknown> = {};
  for (const conditionVar of extractIfConditionVars(protectedHtml)) {
    flatContext[conditionVar] = `{{ ${conditionVar} }}`;
  }
  for (const [path, sample] of Object.entries(context)) {
    flatContext[path] = coerceSampleValue(sample);
  }

  try {
    const template = Twig.twig({
      data: protectedHtml,
      rethrow: true,
      debug: false,
      trace: false,
    } as Parameters<typeof Twig.twig>[0]);
    const rendered = template.render(expandFlatContext(flatContext));
    return restoreOutputTags(
      typeof rendered === 'string' ? rendered : String(rendered),
      tags,
    );
  } catch {
    return html;
  }
}

/**
 * The fragment map currently loaded into twig.js's template registry.
 * Fragments register under their key (`partials/xxxl/header`) so
 * `allowInlineIncludes` lookups resolve them; re-registration only happens
 * when the caller passes a different map (the Redux selector memoizes it).
 */
let registeredFragments: Record<string, string> | null = null;

/**
 * The public twig.js API hides the template registry; `Twig.extend` passes
 * the internal core, which is the sanctioned way to reach it. Needed to
 * replace/remove fragment registrations (plain `Twig.twig({id})` silently
 * refuses duplicate ids).
 */
function getTemplateRegistry(): Record<string, unknown> | undefined {
  let registry: Record<string, unknown> | undefined;
  (Twig as any).extend((core: any) => {
    registry = core.Templates?.registry;
  });
  return registry;
}

/**
 * Registers only what actually changed.
 *
 * The identity check above catches a repeated render with the same map, but any
 * caller that *derives* a map pays full price on every keystroke — the fragment
 * editor overlays its unsaved buffer with
 * `{ ...fragmentSources, [draftKey]: draftContent }`, so one changed character
 * produced a brand-new object and re-parsed all 55 fragments through twig.js.
 *
 * Measured: re-registering the whole catalog is **6.43 ms**, while rendering the
 * template itself is **0.09 ms** — the registration was ~70× the work it was
 * setting up for. Diffing turns that into one re-parse of the single fragment
 * that changed.
 *
 * Removals still have to be handled, or a renamed or deleted fragment would
 * linger in the registry and mask a "template not found" fallback.
 */
function registerFragments(fragments: Record<string, string>): void {
  if (registeredFragments === fragments) return;

  const registry = getTemplateRegistry();
  const previous = registeredFragments;

  if (registry && previous) {
    for (const key of Object.keys(previous)) {
      if (!(key in fragments)) delete registry[key];
    }
  }
  registeredFragments = fragments;

  for (const [key, source] of Object.entries(fragments)) {
    // Unchanged and already compiled — the expensive part is the parse, and it
    // would produce exactly the same template.
    if (previous && previous[key] === source && registry && registry[key]) {
      continue;
    }
    if (registry) delete registry[key];
    try {
      Twig.twig({
        id: key,
        data: escapeBraceLiterals(source),
        allowInlineIncludes: true,
        rethrow: true,
      } as Parameters<typeof Twig.twig>[0]);
    } catch {
      // A single broken fragment must not take down the whole preview; the
      // templates referencing it will hit the naive fallback instead.
    }
  }
}

let filtersRegistered = false;

function registerFilters(): void {
  if (filtersRegistered) return;
  filtersRegistered = true;

  // Server parity (EmailTemplate::translationResolver): a key present in the
  // partner catalog resolves to its value even when that value is empty —
  // templates rely on '' to hide optional blocks. Only a key with no catalog
  // entry at all stays raw-looking.
  Twig.extendFilter('trans', (value: unknown) => {
    const key = String(value ?? '');
    const translation = activeTranslations[key];
    return translation != null ? translation : `{{ '${key}' | trans }}`;
  });

  Twig.extendFilter('html_entity_decode', (value: unknown) =>
    decodeHtmlEntities(String(value ?? '')),
  );

  registerIncludeFunction();
  patchStringLiteralUnescaping();
}

const STRING_ESCAPES: Record<string, string> = { n: '\n', r: '\r', t: '\t' };

/**
 * twig.js 3 unescapes a string literal with `String.replace('\\\'', '\'')`: only
 * the first `\'` is unescaped, and `\"` inside single quotes is never unescaped.
 * PHP Twig unescapes the whole literal (`stripcslashes`), and templates pass
 * HTML attributes through partial parameters (`'content':'<a href=\"…\">'`), so
 * without this patch the preview renders `href=\"…\"` and the link breaks.
 */
function patchStringLiteralUnescaping(): void {
  (Twig as any).extend((core: any) => {
    const handler = core.expression.handler[core.expression.type.string];
    handler.compile = (
      token: { value: string; match?: unknown },
      _stack: unknown,
      output: unknown[],
    ) => {
      delete token.match;
      token.value = token.value
        .slice(1, -1)
        .replace(/\\([\s\S])/g, (_, char: string) => STRING_ESCAPES[char] ?? char);
      output.push(token);
    };
  });
}

/**
 * twig.js ships the `{% include %}` *tag* and the `template_from_string()`
 * function, but not the `include()` *function* PHP Twig has. The whole
 * `general/*` partial family is written as
 * `{{ include(template_from_string(content)) | raw }}` — the idiom that lets a
 * partial render Twig passed to it as a parameter — so without this every one
 * of them dies with "include function does not exist" and the preview drops to
 * the naive substitution fallback.
 *
 * Mirrors PHP Twig's signature: `include(template, variables = {}, with_context = true)`.
 */
function registerIncludeFunction(): void {
  Twig.extendFunction(
    'include',
    function (
      this: { context?: Record<string, unknown> },
      template: unknown,
      variables?: Record<string, unknown>,
      withContext = true,
    ) {
      const context = withContext
        ? { ...(this?.context ?? {}), ...(variables ?? {}) }
        : { ...(variables ?? {}) };

      // template_from_string() returns a compiled twig.js template.
      const compiled = template as { render?: (c: unknown) => string };
      if (typeof compiled?.render === 'function') {
        return compiled.render(context);
      }

      // A plain string is a fragment key, same as {% include 'partials/x' %}.
      const registered = getTemplateRegistry()?.[String(template ?? '')] as
        | { render?: (c: unknown) => string }
        | undefined;

      return typeof registered?.render === 'function'
        ? registered.render(context)
        : '';
    } as Parameters<typeof Twig.extendFunction>[1],
  );
}

/**
 * Expands the flat dot-path map into the nested object Twig reads
 * ({"customer.firstname": "Max"} → {customer: {firstname: "Max"}}). When a
 * shorter path already claimed a node as a string value, deeper paths under
 * it are skipped rather than clobbering it.
 */
export function expandFlatContext(
  flat: Record<string, unknown>,
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [path, value] of Object.entries(flat)) {
    const segments = path.split('.').filter((segment) => segment !== '');
    if (segments.length === 0) continue;

    let node = root;
    let blocked = false;
    for (const segment of segments.slice(0, -1)) {
      const existing = node[segment];
      if (existing == null) {
        const child: Record<string, unknown> = {};
        node[segment] = child;
        node = child;
      } else if (typeof existing === 'object') {
        node = existing as Record<string, unknown>;
      } else {
        blocked = true;
        break;
      }
    }
    if (blocked) continue;

    const leaf = segments[segments.length - 1];
    if (typeof node[leaf] !== 'object' || node[leaf] == null) {
      node[leaf] = value;
    }
  }
  return root;
}

/**
 * A body that is already a complete HTML document (imported marketing
 * emails) must never be wrapped in the partner's default layout — a Twig
 * `{% extends %}` discards all content outside `{% block %}` tags, which
 * would erase the whole email. Mirrors EmailTemplate::isCompleteHtmlDocument
 * on the server; an explicitly chosen layout still wins.
 */
export function isCompleteHtmlDocument(source: string): boolean {
  return /<!DOCTYPE\s|<html[\s>]/i.test(source);
}

/**
 * Sample values are strings from text inputs; Twig conditions need real
 * booleans/zero to go falsy ('false' typed into a toggle input must hide the
 * branch, not render as truthy text).
 */
function coerceSampleValue(value: string): string | number | boolean {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === '0') return 0;
  return value;
}

const TEMPLATE_REFERENCE_PATTERN =
  /\{%-?\s*(?:include|extends|embed|use)\s*\(?\s*(['"])([^'"]+)\1/g;

/**
 * The template sources reachable from `source`: the layout and every
 * fragment referenced (transitively) via include/extends/embed with a string
 * literal. Condition variables are extracted from these so `{% if %}`
 * branches living inside fragments are toggleable from the sample panel even
 * though twig.js consumes the tags during rendering.
 */
function collectReachableSources(
  source: string,
  layoutKey: string | null,
  fragments: Record<string, string>,
): string[] {
  const sources = [source];
  const queue: string[] = layoutKey ? [layoutKey] : [];
  for (const match of source.matchAll(TEMPLATE_REFERENCE_PATTERN)) {
    queue.push(match[2]);
  }

  const visited = new Set<string>();
  while (queue.length > 0) {
    const key = queue.shift() as string;
    if (visited.has(key)) continue;
    visited.add(key);

    const fragmentSource = fragments[key];
    if (fragmentSource == null) continue;
    sources.push(fragmentSource);
    for (const match of fragmentSource.matchAll(TEMPLATE_REFERENCE_PATTERN)) {
      queue.push(match[2]);
    }
  }
  return sources;
}

/**
 * Renders the template with twig.js. On any compile or runtime error (bad
 * syntax mid-edit, unresolvable includes/extends) the naive substitution
 * fallback keeps the preview alive and the error message is surfaced to the
 * caller.
 */
export function renderTwigPreview(
  source: string,
  options: TwigPreviewOptions = {},
): TwigPreviewResult {
  const context = options.context ?? {};
  const translations = options.translations ?? {};
  const layoutKey = options.layoutKey || null;

  registerFilters();
  registerFragments(options.fragments ?? {});

  const prefilled: Record<string, string> = {};
  for (const prefillSource of [source, ...(options.prefillSources ?? [])]) {
    for (const path of [
      ...extractVariablePaths(prefillSource),
      // Also the `'Hi ' ~ (customer.firstname)` form the compiler emits for text that
      // mixes words with a variable; without this such a variable renders as nothing.
      ...extractConcatenatedPaths(prefillSource),
    ]) {
      prefilled[path] = `{{ ${path} }}`;
    }
  }
  const coercedSamples: Record<string, unknown> = {};
  for (const [path, sample] of Object.entries(context)) {
    coercedSamples[path] = coerceSampleValue(sample);
  }
  const renderContext: Record<string, unknown> = expandFlatContext({
    ...prefilled,
    ...coercedSamples,
  });

  // Condition variables live in the source and reachable fragments — the
  // rendered output no longer carries real `{% if %}` tags, so the sample
  // panel's toggle rows must come from the sources themselves.
  const sourceConditionVars: string[] = [];
  for (const reachableSource of collectReachableSources(
    source,
    layoutKey,
    options.fragments ?? {},
  )) {
    for (const conditionVar of extractIfConditionVars(reachableSource)) {
      if (!sourceConditionVars.includes(conditionVar)) {
        sourceConditionVars.push(conditionVar);
      }
    }
  }

  // Backend parity (EmailTemplate::renderEmail): the layout wraps the body
  // via an injected extends, and reads the rendered subject/pretext.
  const escapedSource = escapeBraceLiterals(source);
  const renderSource = layoutKey
    ? `{% extends '${layoutKey}' %}\n${escapedSource}`
    : escapedSource;
  if (layoutKey) {
    renderContext._subject = options.subjectText ?? '';
    renderContext._pretext = options.pretextText ?? '';
  }

  // Rendered output still carries provider-side artifacts as literal text:
  // merge tags restored from brace literals ({{ Event_start_date }}) and
  // literal merge conditionals. Conditionals go through twig.js again, sample
  // values substitute into the merge tags, comments the consumed tags left
  // empty are dropped, and the fillable variables are reported for the sample
  // panel — extracted before substitution so rows stay stable while typing.
  const finalize = (
    rendered: string,
    evaluateConditionals = true,
  ): Omit<TwigPreviewResult, 'error'> => {
    const withBraces = restoreBraceLiterals(rendered);
    const conditionVariables = [...sourceConditionVars];
    for (const conditionVar of extractIfConditionVars(withBraces)) {
      if (!conditionVariables.includes(conditionVar)) {
        conditionVariables.push(conditionVar);
      }
    }
    // The fallback path hands us the *unrendered* source, whose `{% %}` tags
    // are the ones twig.js just choked on: evaluating them would only spend a
    // second failing compile, so they stay visible next to the reported error.
    const evaluated = evaluateConditionals
      ? evaluateLiteralConditionals(withBraces, context)
      : withBraces;
    return {
      html: stripEmptyComments(substitutePreviewVars(evaluated, context)),
      placeholderVariables: extractVariablePaths(evaluated),
      conditionVariables,
    };
  };

  activeTranslations = translations;
  try {
    const template = Twig.twig({
      data: renderSource,
      allowInlineIncludes: true,
      rethrow: true,
      // Don't let mid-edit syntax errors leak to the console; the fallback
      // and the returned error message carry the signal.
      debug: false,
      trace: false,
    } as Parameters<typeof Twig.twig>[0]);
    const html = template.render(renderContext);
    return finalize(typeof html === 'string' ? html : String(html));
  } catch (caught) {
    const message =
      caught instanceof Error ? caught.message : String(caught ?? 'Twig error');
    return {
      ...finalize(substituteTransFilters(escapedSource, translations), false),
      error: message,
    };
  } finally {
    activeTranslations = {};
  }
}
